import AlgoliaTracking from "./algolia-tracking";
import ScrollCarousel from "./scroll-carousel";
import Helpers from "./helpers";
import ProductGridLoad from "./product-grid-load";
import SelectorFilter from "./selector-filter";
import Tracking from "./tracking";

declare var Mustache: any;
declare var topSellersB: any;
declare var topSellers: any;

export default class LoadContent {

	private currentElement: HTMLElement;
	private lazyLoadItemsMax: number;
	private abortController: AbortController;
	private placeholderContent: string;

	constructor(currentElement: HTMLElement) {

		this.currentElement = currentElement;
		if (this.currentElement.getAttribute('data-load-initial-items-max')) {
			this.lazyLoadItemsMax = parseInt(this.currentElement.getAttribute('data-load-initial-items-max'));
		} else {
			this.lazyLoadItemsMax = 8;
		}
		if (this.currentElement.hasAttribute('data-load-instant')) {
			this.load();
		} else {
			this.watchForEnteringViewport();
		}

		this.watchForAttributeChanges();
	
	}

	private watchForEnteringViewport() {

		const self = this;
		let observer = new IntersectionObserver(observerCallback, { rootMargin: "0px 0px 500px 0px" });
		function observerCallback(entries, observer) {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					self.load();
					observer.unobserve(self.currentElement);
				}
			});
		};
		observer.observe(this.currentElement);

	}

	private watchForAttributeChanges() {
		const self = this;
		const observerOptions = {
			childList: false,
			attributes: true,
			subtree: false
		}
		const observer = new MutationObserver(observerCallback);
		function observerCallback(mutationList, observer) {
			mutationList.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'data-load-content') {
					const content = self.currentElement.querySelector('.js-load-replace');
					if (self.placeholderContent) {
						content.innerHTML = self.placeholderContent;
					}
					self.currentElement.style.display = 'block';
					const hadPreviouslyLoaded = self.currentElement.classList.contains('js-load-content-done');
					if (hadPreviouslyLoaded) {
						self.currentElement.classList.remove('js-load-content-done');
						self.watchForEnteringViewport();
					}
				}
			});
		};
		observer.observe(this.currentElement, observerOptions);
	}

	private load() {

		const scope = this;
		const element = this.currentElement;

		const content = element.querySelectorAll('.js-load-replace');
		this.placeholderContent = content.length ? content[0].innerHTML : '';
		const url = element.getAttribute('data-load-content');
		const key = element.getAttribute('data-load-key');
		const store = element.getAttribute('data-load-store') || false;
		const listName = element.getAttribute('data-load-list-name');

		if (!url || !key) { return; }

		scope.getContent(url, element).then(function (response: any) {

			const displayValue = response[key].length ? 'block' : 'none';
			const wrapperContainer = element.closest('.js-load-container') as HTMLElement;
			element.style.display = displayValue;
			if (wrapperContainer) { wrapperContainer.style.display = displayValue; }
			if (!response[key].length) { return; }

			//@ts-ignore
			if (store) { SiteStore.loadContent[store] = response[key]; }

			const initialItems = response[key].slice(0, scope.lazyLoadItemsMax);
			const lazyItems = response[key].slice(scope.lazyLoadItemsMax);
			response[key] = initialItems;

			Helpers.scopedLoop(content, function (item: HTMLElement, i: Number, context: any) {

				scope.setAlgoliaQueryID(element, response);
				const template = item.getAttribute('data-load-template');
				const templateWrapper = item.getAttribute('data-load-template-wrapper');

				const templateContent = document.querySelector(template).innerHTML;
				Mustache.parse(templateContent);

				if (templateWrapper) {

					const templateWrapperContent = document.querySelector(templateWrapper).innerHTML;
					Mustache.parse(templateWrapperContent);
					item.innerHTML = Mustache.render(templateWrapperContent, response);

					const deepestElement = scope.getDeepestElement(item);
					deepestElement.innerHTML = Mustache.render(templateContent, response);

				} else {

					item.innerHTML = Mustache.render(templateContent, response);

				}

				response[key] = lazyItems;
				scope.initialiseComponents(element, response, key, templateContent, listName);

			}, this);

		}).catch((e) => {
			if (!this.requestHasAborted()) {
				element.style.display = 'none';
			}
		});

		element.classList.add('js-load-content-done');

	}

	private setAlgoliaQueryID(element: HTMLElement, response: any) {
		if (
			response &&
			response.Attributes &&
			typeof response.Attributes.QueryID !== "undefined" &&
			response.Attributes.QueryID !== null
		) {
			element.setAttribute('data-algolia-query-id', response.Attributes.QueryID)
		}
	}

	private requestHasAborted() {
		return this.abortController && this.abortController.signal.aborted;
	}

	private getContent(url: any, element: HTMLElement) {

		this.abort();
		this.abortController = new AbortController();

		return fetch(url, {
			signal: this.abortController.signal
		}).then((response)=> {

			if (response.ok) {
				this.abortController = null;
				return response.json();
			} else {
				element.style.display = 'none';
				throw new Error(response as any);

			}

		});

	}

	private initialiseComponents(container: HTMLElement, response: any, key: string, template: string, listName: string) {

		if (typeof topSellersB !== 'undefined') {
			topSellersB.init();
		}
		if (typeof topSellers !== 'undefined') {
			topSellers.init(response[key].length ? response : null, template || null);
		}
		if (typeof ScrollCarousel !== 'undefined') {
			const scrollCarousel = SelectorFilter.excludeByClass(container.querySelectorAll('.js-scroll-carousel:not(.js-scroll-carousel-done)'), 'js-scroll-carousel-done')[0];
			
			if (scrollCarousel) {
				setTimeout(function() {
					new ScrollCarousel(scrollCarousel, response[key].length ? response : null, template || null, key || null);
				}, 100);
			}
		}
		if (typeof ProductGridLoad !== 'undefined') {
			const productGrid = this.currentElement.querySelector('.js-product-grid-load') as HTMLElement;
			if (productGrid) {
				new ProductGridLoad(productGrid, response[key].length ? response : null);
			}
			
		}

		const triggerUpdateWishlistButtons = Helpers.createCustomEventForDispatch('updateWishlistButtons');
		document.dispatchEvent(triggerUpdateWishlistButtons);

		const lowercaseKey = key.toLowerCase();
		if (lowercaseKey === "products") {
			if (response[key].length) {
				this.trackListView(listName, response[key]);
			}
			setTimeout(() => {
				this.attachSelectedItemEvent(container, listName);
			}, 150); // rendering delay
		}

	}

	private getDeepestElement(item: HTMLElement) {

		const children = Array.prototype.slice.call(item.querySelectorAll('*'));
		return children.pop();

	}

	public abort() {
		if (this.abortController) {
			this.abortController.abort();
		}
	}

	private trackListView(listName: string, products: any) {

		let items = [];
		for (var i = 0; i < products.length; i++) {
			const currentLineItem = products[i];
			items.push({
				item_id: currentLineItem.Name,
				item_name: currentLineItem.DisplayName,
				price: currentLineItem.Price.Price
			});
		}
		Tracking.trackListView(listName.toLowerCase().replace(/ /g, "_"), listName, items);

	}
	private attachSelectedItemEvent(container: HTMLElement, listName: string) {
		container.querySelectorAll('.js-product-link').forEach(item => {
			item.addEventListener('click', event => {
				if (!item.classList.contains('js-product-link--clicked')) {
					this.trackSelectItem(item as HTMLElement, listName);
					item.classList.add('js-product-link--clicked');
				}
			});
		});
	}

	private getAlgoliaQueryIDOverride(element: HTMLElement) {
		const possibleAlgoliaQueryIDElement = element.closest('[data-algolia-query-id]');
		if (possibleAlgoliaQueryIDElement && possibleAlgoliaQueryIDElement.hasAttribute('data-algolia-query-id')) {
			return possibleAlgoliaQueryIDElement.getAttribute('data-algolia-query-id');
		}
		return undefined;
	}

	private trackSelectItem(element: HTMLElement, listName: string) {
		const elementParent = element.closest('.js-product') as HTMLDivElement;
		const item = Tracking.getItemDetails(elementParent);
		const isVariant = elementParent.getAttribute('data-product-has-variants');
		Tracking.trackSelectItem(listName.toLowerCase().replace(/ /g, "_"), listName, item);
		const productPosition = Helpers.getElementIndex(element.closest('li')) + 1;
		const algoliaSource = element.closest('[data-algolia-source]') || undefined;
		const eventName = algoliaSource ? `${algoliaSource.getAttribute("data-algolia-source")} Product Clicked` : undefined;
		if (!isVariant) {
			AlgoliaTracking.clickedObjectID(elementParent.getAttribute('data-product-guid'), productPosition, eventName, this.getAlgoliaQueryIDOverride(element));
		}
	}

}
